/** Price bits shared by search, cart and checkout: first-year offers, struck-through usual prices. */
import { cx, Badge } from "@/components/ui";
import { endingOf, noVatReason, promotionText, type CartItem } from "@/lib/domains";
import { formatMoney } from "@/lib/format";

/** "Offer until 31 Oct 2026" — beside a promotional first-year price. */
export function OfferPill({
  promotion,
}: {
  promotion: { promotion_label: string | null; promotion_ends_at: string | null };
}) {
  return (
    <Badge tone="info" size="sm">
      {promotionText(promotion)}
    </Badge>
  );
}

/** The usual first-year price, struck through next to an offer (read out as "usually £X"). */
export function UsualPrice({
  cents,
  currency,
  className,
}: {
  cents: number;
  currency: string;
  className?: string;
}) {
  return (
    <s className={cx("text-fg-subtle", className)}>
      <span className="sr-only">usually </span>
      {formatMoney(cents, currency)}
    </s>
  );
}

/** "No VAT on .eu (zero-rated)" for each ending in the cart that isn't standard-rated. */
export function NoVatNote({ items, className }: { items: CartItem[]; className?: string }) {
  const endings = new Map<string, string>();
  for (const i of items) {
    const why = noVatReason(i.tax);
    if (why) endings.set(endingOf(i.fqdn), why);
  }
  if (endings.size === 0) return null;
  return (
    <ul className={cx("flex flex-col gap-0.5 text-xs text-fg-subtle", className)}>
      {[...endings].map(([ending, why]) => (
        <li key={ending}>
          No VAT on {ending} ({why})
        </li>
      ))}
    </ul>
  );
}
